import React, { useEffect, useState } from "react";
import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ValidationScoringList from "./ValidationScoringList";
import ComparisonScoringList from "./ComparisonScoringList";
import GenericRQScoringList from "./GenericRQScoringList";
import ValidationRunScoringList from "./ValidationRunScoringList";
import ComparisonRunScoringList from "./ComparisonRunScoringList";
import ResearchQueryRunScoringList from "./ResearchQueryRunScoringList";
import PipelineWeightConfigList from "./PipelineWeightConfigList";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { useAuth } from "../../contexts/AuthContext";

// URL parameter keys used to persist tab state. These are reserved by
// ScoringConfig and preserved by the child list components when they
// rewrite their own filter params to the URL.
const URL_PARAM_TAB = "tab";
const URL_PARAM_RUN_SUB = "runSub";
const URL_PARAM_AGG_SUB = "aggSub";

const parseTabParam = (v: string | null): number => {
  if (v === null) return 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

// ── Shared tab style helpers ──────────────────────────────────────────────────

const parentTabSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "0.95rem",
  color: "text.secondary",
  "&.Mui-selected": { color: "var(--orange)", fontWeight: 700 },
};

const subTabSx = {
  textTransform: "none" as const,
  fontWeight: 400,
  fontSize: "0.875rem",
  color: "text.secondary",
  "&.Mui-selected": { color: "var(--auto-sky-blue)", fontWeight: 600 },
};

const tabIndicatorSx = {
  "& .MuiTabs-indicator": { backgroundColor: "var(--auto-sky-blue)", height: 3 },
};

// ── Component ─────────────────────────────────────────────────────────────────

const ScoringConfig = () => {
  const { t } = useAppTranslation();
  const { hasPermission, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Permission guard — admins bypass all checks
  const canView = (entity: string): boolean =>
    isAdmin || hasPermission(entity, "view");

  // ── Tab definitions filtered by permission ────────────────────────────────

  const runSubTabDefs = [
    {
      entity: "validation_scoring_config",
      label: t("scoringConfig.tabs.validationScoringConfig"),
      component: <ValidationScoringList />,
    },
    {
      entity: "comparison_scoring_config",
      label: t("scoringConfig.tabs.comparisonScoringConfig"),
      component: <ComparisonScoringList />,
    },
    {
      entity: "research_query_scoring_config",
      label: t("scoringConfig.tabs.genericRQScoringConfig"),
      component: <GenericRQScoringList />,
    },
  ].filter((tab) => canView(tab.entity));

  const aggSubTabDefs = [
    {
      entity: "validation_run_scoring_config",
      label: t("scoringConfig.tabs.validationRunScoringConfig"),
      component: <ValidationRunScoringList />,
    },
    {
      entity: "comparison_run_scoring_config",
      label: t("scoringConfig.tabs.comparisonRunScoringConfig"),
      component: <ComparisonRunScoringList />,
    },
    {
      entity: "research_query_run_scoring_config",
      label: t("scoringConfig.tabs.researchQueryRunScoringConfig"),
      component: <ResearchQueryRunScoringList />,
    },
    {
      entity: "run_scoring_pipeline_weight_config",
      label: t("scoringConfig.tabs.pipelineWeightConfig"),
      component: <PipelineWeightConfigList />,
    },
  ].filter((tab) => canView(tab.entity));

  // A parent tab is only visible when it has at least one accessible sub-tab
  const parentTabDefs = [
    {
      id: "run",
      label: t("scoringConfig.tabs.runScoreConfig"),
      subTabs: runSubTabDefs,
    },
    {
      id: "agg",
      label: t("scoringConfig.tabs.aggregationScoreConfig"),
      subTabs: aggSubTabDefs,
    },
  ].filter((p) => p.subTabs.length > 0);

  // ── Tab state (indices into the *visible* arrays) ─────────────────────────

  // 0 = Run Score Config | 1 = Aggregation Score Config
  const [parentTab, setParentTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_TAB))
  );
  // Sub-tab indices are independent per parent so switching parents restores position
  const [runSubTab, setRunSubTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_RUN_SUB))
  );
  const [aggSubTab, setAggSubTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_AGG_SUB))
  );

  // Clamp indices so they never exceed the bounds of the visible arrays
  const safeParentTab = Math.max(
    0,
    Math.min(parentTab, parentTabDefs.length - 1)
  );
  const safeRunSubTab = Math.max(
    0,
    Math.min(runSubTab, runSubTabDefs.length - 1)
  );
  const safeAggSubTab = Math.max(
    0,
    Math.min(aggSubTab, aggSubTabDefs.length - 1)
  );

  // URL → State: keep tab state in sync with browser back/forward navigation.
  // Only updates state when the corresponding param is present in the URL,
  // so that the inactive parent's subtab position is preserved across
  // parent‑tab switches that intentionally omit the other subtab key.
  useEffect(() => {
    const tabParam = searchParams.get(URL_PARAM_TAB);
    const runSubParam = searchParams.get(URL_PARAM_RUN_SUB);
    const aggSubParam = searchParams.get(URL_PARAM_AGG_SUB);

    if (tabParam !== null) {
      const v = parseTabParam(tabParam);
      if (v !== parentTab) setParentTab(v);
    }
    if (runSubParam !== null) {
      const v = parseTabParam(runSubParam);
      if (v !== runSubTab) setRunSubTab(v);
    }
    if (aggSubParam !== null) {
      const v = parseTabParam(aggSubParam);
      if (v !== aggSubTab) setAggSubTab(v);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab change handlers push a new history entry (so the browser back button
  // returns to the previous tab) and reset filter params since the active
  // child list component changes.
  const handleParentTabChange = (_: React.SyntheticEvent, v: number) => {
    setParentTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, String(v));
    const activeDef = parentTabDefs[v];
    if (activeDef?.id === "run") next.set(URL_PARAM_RUN_SUB, String(runSubTab));
    else next.set(URL_PARAM_AGG_SUB, String(aggSubTab));
    setSearchParams(next);
  };

  const handleRunSubTabChange = (_: React.SyntheticEvent, v: number) => {
    setRunSubTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, String(safeParentTab));
    next.set(URL_PARAM_RUN_SUB, String(v));
    setSearchParams(next);
  };

  const handleAggSubTabChange = (_: React.SyntheticEvent, v: number) => {
    setAggSubTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, String(safeParentTab));
    next.set(URL_PARAM_AGG_SUB, String(v));
    setSearchParams(next);
  };

  // Resolve the active parent and its sub-tab state
  const activeParentDef = parentTabDefs[safeParentTab];
  const activeSubTab =
    activeParentDef?.id === "run" ? safeRunSubTab : safeAggSubTab;
  const handleSubTabChange =
    activeParentDef?.id === "run"
      ? handleRunSubTabChange
      : handleAggSubTabChange;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {t("scoringConfig.header")}
      </Typography>

      {parentTabDefs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("scoringConfig.noPermission")}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          {/* ── Parent tabs ────────────────────────────────────────────────── */}
          <Tabs
            value={safeParentTab}
            onChange={handleParentTabChange}
            sx={{
              ...tabIndicatorSx,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "grey.50",
            }}
          >
            {parentTabDefs.map((def) => (
              <Tab key={def.id} label={def.label} sx={parentTabSx} />
            ))}
          </Tabs>

          {/* ── Sub-tabs and content for the active parent ─────────────────── */}
          {activeParentDef && (
            <>
              <Tabs
                value={activeSubTab}
                onChange={handleSubTabChange}
                sx={{
                  ...tabIndicatorSx,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                {activeParentDef.subTabs.map((tab) => (
                  <Tab key={tab.entity} label={tab.label} sx={subTabSx} />
                ))}
              </Tabs>
              <Box sx={{ pt: 1 }}>
                {activeParentDef.subTabs[activeSubTab]?.component}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ScoringConfig;
